// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  ControleCaixas.tsx
//  Extrato de caixa + reforço + sangria por caixa operacional.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  TrendingUp, TrendingDown, Wallet, ArrowUpCircle, ArrowDownCircle,
  Plus, Minus, RefreshCw, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const PAGE_SIZES = [10, 20, 50] as const;
import { useCaixaExtrato, useCaixaReforco, useCaixaSangria } from '@/features/caixa/hooks/useCaixa';
import type { ReforcoSangriaRequest } from '@/features/caixa/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

// ── Caixas cadastrados (fixo por enquanto; virá de /api/caixa quando disponível) ──
const CAIXAS = [
  { id: 1, nome: 'Caixa 01' },
  { id: 2, nome: 'Caixa 02' },
  { id: 3, nome: 'Caixa 03' },
];

type Tipo = 'reforco' | 'sangria' | null;

const hoje   = new Date().toISOString().slice(0, 10);
const primeiroDia = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  .toISOString().slice(0, 10);

export default function ControleCaixas() {
  const [caixaId, setCaixaId]     = useState(CAIXAS[0].id);
  const [dtInicio, setDtInicio]   = useState(primeiroDia);
  const [dtFim, setDtFim]         = useState(hoje);
  const [tipo, setTipo]           = useState<Tipo>(null);
  const [valor, setValor]         = useState('');
  const [descricao, setDescricao] = useState('');
  const [dtMov, setDtMov]         = useState(hoje);

  const { data: extrato, isLoading, refetch } = useCaixaExtrato(caixaId, dtInicio, dtFim);
  const reforcoMut  = useCaixaReforco(caixaId);
  const sangriaMut  = useCaixaSangria(caixaId);

  const caixaNome = CAIXAS.find((c) => c.id === caixaId)?.nome ?? `Caixa ${caixaId}`;
  const lancamentos = extrato?.lancamentos ?? [];

  // ── Paginação local ───────────────────────────────────────────────────────
  const [pag,           setPag]           = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const total     = lancamentos.length;
  const totalPag  = Math.max(1, Math.ceil(total / tamanhoPagina));
  const slice     = lancamentos.slice((pag - 1) * tamanhoPagina, pag * tamanhoPagina);
  const firstItem = total === 0 ? 0 : (pag - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pag * tamanhoPagina, total);
  const handlePageSizeChange = (value: string) => { setTamanhoPagina(Number(value)); setPag(1); };

  // ── Submit reforço/sangria ────────────────────────────────────────────────
  function handleSubmit() {
    const req: ReforcoSangriaRequest = {
      valor:       parseFloat(valor.replace(',', '.')),
      descricao,
      dtMovimento: dtMov,
    };
    if (tipo === 'reforco') {
      reforcoMut.mutate(req, { onSuccess: () => closeDialog() });
    } else if (tipo === 'sangria') {
      sangriaMut.mutate(req, { onSuccess: () => closeDialog() });
    }
  }

  function openDialog(t: 'reforco' | 'sangria') {
    setTipo(t);
    setValor('');
    setDescricao('');
    setDtMov(hoje);
  }
  function closeDialog() { setTipo(null); }

  const isSubmitting = reforcoMut.isPending || sangriaMut.isPending;
  const valorNum     = parseFloat(valor.replace(',', '.'));
  const canSubmit    = !isNaN(valorNum) && valorNum > 0 && descricao.trim().length >= 3;

  const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="p-6 space-y-6">
      {/* ── Cabeçalho ── */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Controle de Caixas</h1>
          <p className="text-muted-foreground mt-1">Extrato, reforços e sangrias por caixa</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" className="gap-2" onClick={() => openDialog('reforco')}>
            <Plus className="h-4 w-4" /> Reforço
          </Button>
          <Button variant="secondary" className="gap-2" onClick={() => openDialog('sangria')}>
            <Minus className="h-4 w-4" /> Sangria
          </Button>
          <Button variant="outline" size="icon" onClick={() => refetch()} title="Atualizar">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ── Filtros ── */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label>Caixa</Label>
              <div className="flex gap-2">
                {CAIXAS.map((c) => (
                  <Button
                    key={c.id}
                    size="sm"
                    variant={caixaId === c.id ? 'default' : 'outline'}
                    onClick={() => { setCaixaId(c.id); setPag(1); }}
                  >
                    {c.nome}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="dtIni">De</Label>
              <Input id="dtIni" type="date" value={dtInicio} onChange={(e) => { setDtInicio(e.target.value); setPag(1); }} className="w-36" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="dtFim">Até</Label>
              <Input id="dtFim" type="date" value={dtFim} onChange={(e) => { setDtFim(e.target.value); setPag(1); }} className="w-36" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Wallet className="h-4 w-4" /> Saldo Atual — {caixaNome}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-32" /> : (
              <div className={`text-2xl font-bold ${(extrato?.saldo ?? 0) < 0 ? 'text-red-500' : 'text-foreground'}`}>
                {brl(extrato?.saldo ?? 0)}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Créditos no período
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-32" /> : (
              <div className="text-2xl font-bold text-green-500">{brl(extrato?.totalCreditos ?? 0)}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingDown className="h-4 w-4" /> Débitos no período
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-32" /> : (
              <div className="text-2xl font-bold text-red-500">{brl(extrato?.totalDebitos ?? 0)}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Tabela extrato ── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Movimentações — {caixaNome}</CardTitle>
            <span className="text-sm text-muted-foreground">{lancamentos.length} lançamento(s)</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                  <TableHead>Conciliado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : slice.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Nenhum lançamento no período
                    </TableCell>
                  </TableRow>
                ) : slice.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>{new Date(l.dtMovimento).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell className="max-w-xs truncate" title={l.descricao}>{l.descricao}</TableCell>
                    <TableCell>
                      {l.debitoCredito === 'C' ? (
                        <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20 flex items-center gap-1 w-fit">
                          <ArrowUpCircle className="h-3 w-3" /> Crédito
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/10 text-red-600 hover:bg-red-500/20 flex items-center gap-1 w-fit">
                          <ArrowDownCircle className="h-3 w-3" /> Débito
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${l.debitoCredito === 'C' ? 'text-green-600' : 'text-red-600'}`}>
                      {l.debitoCredito === 'D' ? '– ' : '+ '}{brl(l.valor)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {l.saldoAtual != null ? brl(l.saldoAtual) : '—'}
                    </TableCell>
                    <TableCell>
                      {l.isConciliado
                        ? <Badge className="bg-blue-500/10 text-blue-600">Conciliado</Badge>
                        : <Badge variant="outline">Pendente</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPag(1)} disabled={pag === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPag((p) => p - 1)} disabled={pag === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPag) }, (_, i) => {
                  let page: number;
                  if      (totalPag <= 5)       page = i + 1;
                  else if (pag <= 3)             page = i + 1;
                  else if (pag >= totalPag - 2)  page = totalPag - 4 + i;
                  else                           page = pag - 2 + i;
                  return (
                    <Button key={page} variant={pag === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPag(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPag((p) => p + 1)} disabled={pag >= totalPag}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPag(totalPag)} disabled={pag >= totalPag}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pag} de {totalPag}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Dialog Reforço / Sangria ── */}
      <Dialog open={tipo !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {tipo === 'reforco'
                ? <><Plus className="h-5 w-5 text-green-600" /> Reforço de Caixa</>
                : <><Minus className="h-5 w-5 text-red-600" /> Sangria de Caixa</>}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {tipo === 'reforco'
                ? 'Entrada de dinheiro no caixa (ex: troco de abertura, complemento de fundos).'
                : 'Retirada de dinheiro do caixa (ex: depósito bancário, retirada do operador).'}
            </p>
            <div className="space-y-1">
              <Label htmlFor="valorMov">Valor (R$) *</Label>
              <Input
                id="valorMov"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0,00"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="descMov">Descrição *</Label>
              <Input
                id="descMov"
                placeholder="Informe o motivo..."
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                minLength={3}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="dtMovimento">Data do Movimento</Label>
              <Input
                id="dtMovimento"
                type="date"
                value={dtMov}
                onChange={(e) => setDtMov(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              className={tipo === 'reforco' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {isSubmitting ? 'Registrando...' : tipo === 'reforco' ? 'Confirmar Reforço' : 'Confirmar Sangria'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
